import { api, apiRequest, ApiError, USE_MOCK_DATA, buildUrl } from '@/lib/api';
import {
  leadsQueryParams, optionLabel, platformKindLabel, platformNames, platformGroup, LEAD_OPTIONS, PLATFORM_KIND_LABEL, leadCategoriesForLead, categoryName,
  type LeadDetailResponse,
  type LeadEditPatch,
  type LeadQuickUpdate,
  type LeadRecord,
  type LeadsPage,
  type LeadsQuery,
  type LeadChange,
} from '@/data/leads-model';

/* ----------------------------------------------------------------------------
 * Leads — paged list, detail, in-field quick update and the full owner edit
 * (SESSION-COOKIE auth via the shared `api` client).
 *
 *   fetchLeadsPage(q)                       GET   /outreach/leads?project_id=&cursor=&limit=&…
 *   fetchLeadFull(ref)                      GET   /outreach/leads/{ref}
 *   quickUpdateLead(ref, patch, key, pid?)  PATCH /outreach/leads/{ref}[?project_id=]  (quick sheet)
 *   updateLead(ref, patch, key, pid?)       PATCH /outreach/leads/{ref}[?project_id=]  (full edit)
 *
 * ONE idempotency key per user decision: a retry after a flaky connection
 * replays, never double-applies. Domain isolation is SERVER-side: project_id is
 * re-validated for the session workspace (foreign => 422) and a PATCH naming a
 * project that does not own the lead is refused (404). The client never filters
 * domains and never sends internal fields.
 * -------------------------------------------------------------------------- */

export interface LeadUpdateResult { lead: LeadRecord; updated: string[]; changes?: LeadChange[] }

export async function fetchLeadsPage(q: LeadsQuery): Promise<LeadsPage> {
  if (USE_MOCK_DATA) return mockPage(q);
  return api.get<LeadsPage>('/outreach/leads', leadsQueryParams(q));
}

export async function fetchLeadFull(ref: string): Promise<LeadDetailResponse | null> {
  if (USE_MOCK_DATA) {
    const lead = MOCK_LEAD_ROWS.find((l) => l.lead_ref === ref);
    return lead ? { lead, events: [{ event_type: 'IMPORTED', template_id: null, link_id: null, event_at: '2026-10-04 16:20:00', actor: 'operator', detail: 'imported from workbook; captured_via=xlsx_import; project_id=3; batch=mock', changes: null }] } : null;
  }
  try {
    return await api.get<LeadDetailResponse>(`/outreach/leads/${encodeURIComponent(ref)}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** Full owner edit — any owner-editable field; only changed keys should be sent (diffLeadEdit). */
export async function updateLead(ref: string, patch: LeadEditPatch, idempotencyKey: string, projectId?: number | null): Promise<LeadUpdateResult> {
  if (USE_MOCK_DATA) return mockUpdate(ref, patch as Record<string, unknown>);
  return apiRequest<LeadUpdateResult>(`/outreach/leads/${encodeURIComponent(ref)}`, {
    method: 'PATCH', body: patch, idempotencyKey, query: projectId ? { project_id: projectId } : undefined,
  });
}

/** Quick sheet (status / approach / dates / outcome) — same endpoint, same key discipline. */
export async function quickUpdateLead(ref: string, patch: LeadQuickUpdate, idempotencyKey: string, projectId?: number | null): Promise<LeadUpdateResult> {
  return updateLead(ref, patch as LeadEditPatch, idempotencyKey, projectId);
}

/**
 * OWNER CSV export of exactly the current filtered view. Server-side: owner-only,
 * project_id REQUIRED and re-validated for the session workspace, same filters as
 * the list, UTF-8 BOM, formula-injection guard, one audit row per export. The
 * browser receives text/csv and we hand it to the user as a download.
 */
export async function exportLeadsCsv(q: LeadsQuery): Promise<{ filename: string; rows: number | null }> {
  if (!q.projectId) throw new ApiError(400, 'validation_failed', 'Select a domain first — exports are scoped to one project.');
  const params = leadsQueryParams(q);
  delete params.limit; delete params.cursor;
  const url = buildUrl('/outreach/leads/export.csv', params);
  if (USE_MOCK_DATA) return { filename: 'leads-mock.csv', rows: mockPage({ ...q, limit: 10000 }).total };
  const res = await fetch(url, { method: 'GET', credentials: 'include', headers: { Accept: 'text/csv' } });
  if (!res.ok) {
    let code = 'error'; let message = res.statusText;
    try { const j = await res.json(); code = j?.error?.code ?? code; message = j?.error?.message ?? message; } catch { /* non-JSON error body */ }
    throw new ApiError(res.status, code, message);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  const filename = m?.[1] ?? `leads-${new Date().toISOString().slice(0, 10)}.csv`;
  const text = await blob.text();
  const rows = Math.max(0, text.split(/\r\n/).filter((l) => l.trim() !== '').length - 1);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  return { filename, rows };
}

export function newUpdateKey(): string {
  return `lead-update-${crypto.randomUUID()}`.slice(0, 64);
}

// ---- mock fixture (dev only; SYNTHETIC businesses, .example domains) -------
const AREAS = ['West Hampstead', 'Kilburn / Brondesbury', 'Hampstead / Belsize Park', 'Swiss Cottage / Finchley Road / South Hampstead', 'Queens Park / Kensal / St Johns Wood / Maida Vale'];
const DAYS = ['Monday 5 Oct', 'Tuesday 6 Oct', 'Wednesday 7 Oct', 'Thursday 8 Oct', 'Friday 9 Oct'];
const CATS = ['Hair / beauty', 'Beauty / nails / aesthetics', 'Barber / grooming', 'Pilates / yoga / movement', 'Massage / wellness'];
const PLATFORMS: Array<[string, LeadRecord['booking_platform_kind']]> = [['Unknown', 'unknown'], ['Treatwell', 'directory_listing'], ['Fresha', 'directory_listing'], ['Setmore + Treatwell', 'confirmed_system'], ['Own-domain booking + Treatwell', 'direct_website'], ['Unknown', 'unknown'], ['Booksy', 'directory_listing'], ['Own system', 'in_house_system']];
const NAMES = ['Northline Barbers', 'Kalm Studio Pilates', 'Harbour Nails', 'Bluefin Physio', 'Studio Verde Hair', 'Ember Spa', 'Glow Lashes', 'Pine Yoga', 'Maple Row Salon', 'Cedar Lane Clinic', 'Birch & Co Hair', 'Willow Nails', 'Juniper Wellness', 'Aspen Barbers', 'Hazel Beauty Rooms'];
const POSTCODES = ['NW6 3AY', 'NW6 7AB', 'NW3 2QD', 'NW3 5AA', 'NW10 4EE', 'NW6 1XY', 'NW6 2AA', 'NW8 6EB', 'NW6 4HS', 'NW3 4AA'];
export const MOCK_LEAD_ROWS: LeadRecord[] = Array.from({ length: 150 }, (_, i) => {
  const id = i + 1;
  const name = `${NAMES[i % NAMES.length]}${i >= NAMES.length ? ` ${Math.floor(i / NAMES.length) + 1}` : ''}`;
  const [platform, kind] = PLATFORMS[i % PLATFORMS.length];
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 24);
  const hasWeb = i % 3 !== 1;
  const status = i % 11 === 3 ? 'CONTACTED' : i % 13 === 5 ? 'FOLLOWUP_DUE' : 'DISCOVERED';
  const email = i % 2 ? `hello@${slug}.example` : null;
  return {
    lead_ref: `L-${String(1000 + id).padStart(6, '0')}`, status, status_label: optionLabel(LEAD_OPTIONS.statuses, status) ?? status,
    follow_status: 'NOT_FOLLOWED', owner: 'Phoenix', campaign: 'booksesh-partners-week1-2026-10', source_directory: 'BookSesh Partners (booking software) prospects',
    source_url: hasWeb ? `https://${slug}.example/` : 'https://www.google.com/maps/place/example', business_name: name, practitioner_name: null, modality: CATS[i % CATS.length],
    city_region: AREAS[Math.floor(i / 30) % AREAS.length], country: 'UK', business_email: email, email_source_note: null,
    website: hasWeb ? `${slug}.example` : null, instagram_handle: null, linkedin_url: null, facebook_url: null, other_social: null, business_size: null,
    fit_notes: `Approach (workbook): ${i % 19 === 7 ? 'Call / message first; arrange visit' : 'Walk-in prospect'}`, next_action: null,
    next_action_date: status === 'FOLLOWUP_DUE' ? '2026-10-03' : i % 9 === 2 ? '2026-10-09' : null, last_activity_at: null, created_at: '2026-10-04T16:20:00Z', updated_at: '2026-10-04T16:20:00Z',
    is_test_data: false, domain: hasWeb ? `${slug}.example` : null, captured_via: 'xlsx_import', project_id: 3, phone: i % 4 === 0 ? `0207 000 ${String(1000 + id).slice(-4)}` : null,
    address: `${10 + i} Example Road ${POSTCODES[i % POSTCODES.length]}`, postcode: POSTCODES[i % POSTCODES.length], route_day: DAYS[Math.floor(i / 30) % DAYS.length],
    priority: i % 7 === 0 ? 'C' : i % 5 === 0 ? 'B' : 'A', booking_platform: platform, booking_platform_kind: kind,
    platform_evidence_note: kind === 'unknown' ? 'No named platform confirmed publicly' : kind === 'directory_listing' ? 'Public booking page; confirm full business setup with owner' : 'Official booking button links to the vendor',
    platform_evidence_url: kind === 'unknown' ? null : `https://www.treatwell.co.uk/place/${slug}/`, decision_maker: 'Owner / studio manager — ask on visit',
    visit_approach: i % 19 === 7 ? 'phone_call' : 'walk_in', visit_approach_label: i % 19 === 7 ? 'Phone Call' : 'Walk in',
    verification: hasWeb ? 'Business website / booking page found' : 'Directory only — confirm trading before visit', visit_date: status === 'CONTACTED' ? '2026-10-05' : null,
    outcome_note: status === 'CONTACTED' ? 'Owner interested in moving off marketplace commission' : null, subscribed: false, content_shoot_booked: false,
    import_row_ref: `booksesh-150-prospects-week1:${id}`,
    links: { maps: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`, search: `https://www.google.com/search?q=${encodeURIComponent(name)}`, source: hasWeb ? `https://${slug}.example/` : 'https://www.google.com/maps/place/example', ...(kind === 'directory_listing' ? { listings: [`https://www.treatwell.co.uk/place/${slug}/`] } : {}) },
    email_contactable: !!email,
    platform_kind_label: PLATFORM_KIND_LABEL[kind ?? 'unknown'],
    categories: leadCategoriesForLead(CATS[i % CATS.length], name),
    categories_source: 'auto',
    category_labels: leadCategoriesForLead(CATS[i % CATS.length], name).map((c) => categoryName(c)),
  };
});

function mockUpdate(ref: string, patch: Record<string, unknown>): LeadUpdateResult {
  const i = MOCK_LEAD_ROWS.findIndex((l) => l.lead_ref === ref);
  if (i < 0) throw new ApiError(404, 'not_found', 'Unknown lead_ref.');
  const cur = MOCK_LEAD_ROWS[i];
  const entries = Object.entries(patch).filter(([, v]) => v !== undefined);
  const next: LeadRecord = { ...cur, ...Object.fromEntries(entries) } as LeadRecord;
  next.status_label = optionLabel(LEAD_OPTIONS.statuses, next.status) ?? next.status;
  next.visit_approach_label = optionLabel(LEAD_OPTIONS.visitApproaches, next.visit_approach);
  next.platform_kind_label = platformKindLabel(next.booking_platform_kind);
  next.email_contactable = !!next.business_email;
  const changes: LeadChange[] = entries.map(([field, v]) => ({ field, old: cur[field as keyof LeadRecord] == null ? null : String(cur[field as keyof LeadRecord]), new: v == null ? null : String(v) }));
  MOCK_LEAD_ROWS[i] = next;
  return { lead: next, updated: entries.map(([k]) => k), changes };
}

function mockPage(q: LeadsQuery): LeadsPage {
  const scoped = [...MOCK_LEAD_ROWS].reverse().filter(() => !(q.projectId && q.projectId !== 3));
  // Same rule as the server: each facet is counted over the rows filtered by every selector EXCEPT its own.
  const apply = (rows: LeadRecord[], skip: ReadonlyArray<keyof LeadsQuery>): LeadRecord[] => {
    const on = (k: keyof LeadsQuery) => !skip.includes(k) && !!q[k];
    let r = rows;
    if (on('q')) { const n = q.q!.toLowerCase(); r = r.filter((x) => `${x.business_name} ${x.modality} ${x.city_region} ${x.postcode} ${x.booking_platform}`.toLowerCase().includes(n)); }
    if (on('status')) r = r.filter((x) => x.status === q.status);
    if (on('route_day')) r = r.filter((x) => x.route_day === q.route_day);
    if (on('priority')) r = r.filter((x) => x.priority === q.priority);
    if (on('approach')) r = r.filter((x) => x.visit_approach === q.approach);
    if (on('platform_kind')) r = r.filter((x) => x.booking_platform_kind === q.platform_kind);
    if (on('platform')) r = r.filter((x) => platformNames(x.booking_platform).map((n) => n.toLowerCase()).includes(q.platform!.toLowerCase()));
    if (on('platform_group')) r = r.filter((x) => platformNames(x.booking_platform).some((n) => platformGroup(n) === q.platform_group));
    if (on('category')) r = r.filter((x) => (x.categories ?? []).includes(q.category!));
    return r;
  };
  const rows = apply(scoped, []);
  const facets: LeadsPage['facets'] = { status: {}, route_day: {}, priority: {}, booking_platform_kind: {}, visit_approach: {}, platform_name: {}, platform_group: {}, category: {} };
  for (const r of apply(scoped, ['category'])) for (const c of r.categories ?? []) facets.category![c] = (facets.category![c] ?? 0) + 1;
  const own: Record<'status' | 'route_day' | 'priority' | 'visit_approach', keyof LeadsQuery> = { status: 'status', route_day: 'route_day', priority: 'priority', visit_approach: 'approach' };
  for (const k of ['status', 'route_day', 'priority', 'visit_approach'] as const) {
    for (const r of apply(scoped, [own[k]])) { const v = (r as unknown as Record<string, string | null>)[k]; if (v) facets[k][v] = (facets[k][v] ?? 0) + 1; }
  }
  for (const r of apply(scoped, ['platform', 'platform_group', 'platform_kind'])) {
    if (r.booking_platform_kind) facets.booking_platform_kind[r.booking_platform_kind] = (facets.booking_platform_kind[r.booking_platform_kind] ?? 0) + 1;
    const groups = new Set<string>();
    for (const n of platformNames(r.booking_platform)) { facets.platform_name![n] = (facets.platform_name![n] ?? 0) + 1; groups.add(platformGroup(n)); }
    for (const g of groups) facets.platform_group![g] = (facets.platform_group![g] ?? 0) + 1;
  }
  const limit = q.limit ?? 50;
  let start = 0;
  if (q.cursor) { const i = rows.findIndex((r) => r.lead_ref === q.cursor); start = i >= 0 ? i + 1 : 0; }
  const items = rows.slice(start, start + limit);
  return { items, total: rows.length, next_cursor: start + limit < rows.length ? items[items.length - 1]?.lead_ref ?? null : null, scope: { project_id: q.projectId ?? null, host: q.projectId === 3 ? 'booksesh.com' : null }, facets };
}
